import styled from "styled-components"
import {
	MobergAnimationCurve,
	MobergAnimationTiming,
	MobergButton,
	MobergButtonShape,
	MobergFontFamily,
	MobergFontSize,
	MobergIconSize,
	MobergMenuItem,
	MobergTheme,
	MobergThemeColors,
	getColors,
} from "../../../Moberg"
import React from "react"
import { MdDelete, MdMoreVert, MdSettings } from "react-icons/md"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { selectedLayoutAtom, selectedLayoutGroupAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/Layout"
import { Layout } from "../../../Pages/Data/Visualize/DataReview/Types/Layout"
import { useMobergMenu } from "../../../Hooks/useMobergMenu"
import { useLayoutService } from "../../../Hooks/useLayoutService"
import { useModalProvider } from "../../../Providers/ModalProvider"
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { RenameLayoutMenuItem } from "./RenameLayoutMenuItem"
import { CopyLayoutMenuItem } from "./CopyLayoutMenuItem"
import { useDataReviewPermissions } from "../../../Hooks/useDataReviewPermissions"
import { permissionTooltip } from "../../../Utils/permissionUtils"

type TabButtonProps = {
	layout: Layout
	isActive: boolean
	isDragging?: boolean
}

type DisplayTabButtonProps = {
	isActive: boolean
	isDragging?: boolean
	colors: MobergThemeColors
}

export const TabButton = ({ layout, isActive, isDragging }: TabButtonProps) => {
	const setSelectedLayout = useSetRecoilState(selectedLayoutAtom)
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const { removeLayoutFromLayoutGroup, openConfigureLayoutModal } = useLayoutService()
	const actionsMenu = useMobergMenu({ placement: "top-start" })
	const { createModal } = useModalProvider()
	const { canEditGroup } = useDataReviewPermissions()
	const canEditDisplayGroup = canEditGroup(selectedLayoutGroup?.type)

	const confirmDeleteLayout = () => {
		if (!selectedLayoutGroup) {
			return
		}

		actionsMenu.setClickOutsideClose(false)

		createModal(
			<ConfirmationModal
				title={"Delete display?"}
				description={`This will delete ${layout.name} from ${selectedLayoutGroup.name}. This is a permanent action and cannot be undone.`}
				confirmButton={{
					text: "Yes, delete",
					theme: MobergTheme.RED,
					onClick: () => removeLayoutFromLayoutGroup(selectedLayoutGroup, layout.id),
				}}
				afterClose={() => actionsMenu.setClickOutsideClose(true)}
			/>,
			{ zIndex: 10001 }
		)
	}

	const hasMaxDisplays = (selectedLayoutGroup?.layouts.length ?? 0) > 9

	const copyDisabledReason = permissionTooltip(canEditDisplayGroup)
		|| (hasMaxDisplays ? "There cannot be more than 10 different displays." : "")

	return (
		<DisplayTabButton
			isActive={isActive}
			isDragging={isDragging}
			colors={isActive ? getColors(MobergTheme.BLUE) : getColors(MobergTheme.BLACK)}
			onMouseDown={() => setSelectedLayout(layout)}
		>
			<div style={{ display: "flex", gap: "4px", alignItems: "center", height: "100%" }}>
				<h4
					style={{
						fontFamily: MobergFontFamily.REGULAR,
						fontSize: MobergFontSize.REGULAR,
						fontWeight: "bold",
						textAlign: "center",
						margin: 0,
					}}
				>
					{layout.name}
				</h4>

				{/* Because the Moberg button is used as an anchor point for animation, we can't just pop in and out of the screen. 
					This container makes it invisible without conditionally rendering it. */}
			<div style={{
				overflow: "hidden",
				width: isActive ? "24px" : "0px",
				opacity: isActive ? 1 : 0,
				transition: `all ${MobergAnimationTiming.REGULAR} ${MobergAnimationCurve.EASE_IN}`,
			}}>
				{/* className used by index.css to override hover colors on light #9FCAFF tab background */}
				<div className="tab-action-btn">
					<MobergButton
						shape={MobergButtonShape.SQUARE}
						theme={MobergTheme.ON_BLUE}
						onClick={event => actionsMenu.open(event)}
						style={{ padding: "2px", color: "var(--color-tab-active-text, currentColor)" }}
						dataTestId="display-menu-button"
					>
						<MdMoreVert size={MobergIconSize.SMALL} />
					</MobergButton>
				</div>
			</div>

			</div>

			<actionsMenu.MobergMenu style={{ padding: "8px 0", marginBottom: "6px" }}>
				<MobergMenuItem onClick={() => { actionsMenu.close(); openConfigureLayoutModal(layout.id) }} disabled={!canEditDisplayGroup} tooltip={permissionTooltip(canEditDisplayGroup)} dataTestId="display-menu-edit">
					<MdSettings size={MobergIconSize.REGULAR} />
					Edit display
				</MobergMenuItem>

				<RenameLayoutMenuItem
					parentMenu={actionsMenu}
					layout={layout}
					disabled={!canEditDisplayGroup}
					tooltip={permissionTooltip(canEditDisplayGroup)}
				/>

				<CopyLayoutMenuItem
					parentMenu={actionsMenu}
					layout={layout}
					disabled={!!copyDisabledReason}
					tooltip={copyDisabledReason}
				/>

				<MobergMenuItem onClick={confirmDeleteLayout} disabled={!canEditDisplayGroup} tooltip={permissionTooltip(canEditDisplayGroup)} dataTestId="display-menu-delete">
					<MdDelete size={MobergIconSize.REGULAR} />
					Delete
				</MobergMenuItem>
			</actionsMenu.MobergMenu>
		</DisplayTabButton>
	)
}

const DisplayTabButton = styled.div<DisplayTabButtonProps>`
	border-radius: 6px;
	cursor: pointer;
	padding-left: ${({ isActive }) => (isActive ? "16px" : "24px")};
	padding-right: ${({ isActive }) => (isActive ? "8px" : "24px")};
	height: 36px;
	color: ${({ isActive, isDragging }) => (isActive || isDragging ? "var(--color-tab-active-text, var(--text-color))" : "var(--text-color)")};
	border: 1px solid ${({ isActive, isDragging }) => (isActive || isDragging ? "var(--color-tab-active-bg)" : "var(--color-tab-inactive-border, transparent)")};
	transition: background-color ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN}, padding ${MobergAnimationTiming.REGULAR} ${MobergAnimationCurve.EASE_IN};

	background-color: ${({ isActive, isDragging }) => (isActive || isDragging ? "var(--color-tab-active-bg)" : "var(--color-tab-inactive-bg)")};

	&:hover {
		background-color: ${({ isActive }) => isActive ? "var(--color-tab-active-bg)" : "var(--color-tab-hover-bg)"};
		border-color: ${({ isActive }) => isActive ? "var(--color-tab-active-bg)" : "var(--color-tab-hover-bg)"};
		color: ${({ isActive }) => isActive ? "var(--color-tab-active-text, var(--text-color))" : "var(--text-color)"};
	}
`
